import React, { useCallback, useMemo } from 'react';
import { PropTypes } from 'prop-types';

import { useGetDocumentsQuery, useValidateSubscriptionMutation } from 'api/main';
import { RoleNames, roleCategories, stepIDs, subscriptionStatus } from 'utils/constants';
import {
    useMatchAtLeastOneRole,
    useSubscriptionAlerts,
    useSubscriptionCanBeValidated,
    useSubscriptionFormSignatureStatus,
    useSubscriptionValidationStatus,
    useUserRoleMatchCategory,
} from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import InfoBox from 'containers/distributors/distributor-details/InfoBox';
import Alert from './Alert';
import { ActionButton, Comments, Loader, ValidationText } from 'components';
import { Typography } from '@mui/material';

const AlertsSection = ({ subscription, getData, user }) => {
    const userIsServicer = useUserRoleMatchCategory(roleCategories.SERVICERS);
    const userIsProvider = useUserRoleMatchCategory(roleCategories.PROVIDERS);
    const userIsPromoter = useUserRoleMatchCategory(roleCategories.PROMOTERS);
    const userIsNetworkAdmin = useMatchAtLeastOneRole([RoleNames.NETWORK_ADMIN]);
    const userIsStandardServicer = useMatchAtLeastOneRole([RoleNames.SERVICER_STANDARD]);

    const [validateSubscription, { isLoading }] = useValidateSubscriptionMutation();
    const { data: documents = [], isFetching: documentsLoader } = useGetDocumentsQuery(
        { belongsTo: user?.id, userTypes: true, pagination: false },
        { skip: !user?.id }
    );

    const secondIdCheckStatus = useMemo(
        () =>
            subscription?.userTunnelSteps.find(
                ps => ps.tunnelStep.step.id === stepIDs.SECOND_ID_CHECK
            )?.status,
        [subscription]
    );

    const hasSignedBS = useSubscriptionFormSignatureStatus(subscription);
    const isValidated = useSubscriptionValidationStatus(subscription);

    const isSubscriptionFinished = subscription?.subscriptionStatus === subscriptionStatus.FINISHED;
    const isSubscriptionOngoing =
        subscription?.subscriptionStatus === subscriptionStatus.IN_PROGRESS;

    const subscriptionCanBeValidated = useSubscriptionCanBeValidated(subscription, user, documents);
    const subscriptionAlerts = useSubscriptionAlerts(subscription);

    const alertsAreRemovable = useMemo(() => {
        return (
            ((hasSignedBS && isSubscriptionOngoing) || isSubscriptionFinished) &&
            (userIsServicer || userIsProvider || userIsPromoter) &&
            !userIsStandardServicer
        );
    }, [
        hasSignedBS,
        isSubscriptionOngoing,
        isSubscriptionFinished,
        userIsServicer,
        userIsProvider,
        userIsPromoter,
        userIsStandardServicer,
    ]);

    const alerts = useMemo(
        () =>
            !subscription || !subscription.userTunnelSteps
                ? []
                : subscriptionAlerts.filter(
                      a =>
                          !a.solved ||
                          (a.solved &&
                              (a.comment !== null ||
                                  a.alertCode === 121 ||
                                  a.message === 'contact_info.with_co_subscriber' ||
                                  a.message === 'user_knowledge.scores.weak' ||
                                  a.message === 'user_knowledge.level_warning'))
                  ),
        [subscription, subscriptionAlerts]
    );

    const handleValidateSubscription = useCallback(() => {
        validateSubscription(subscription.id);
    }, [subscription, validateSubscription]);

    return (
        <InfoBox title={texts.ALERTS.ALERTS} disableLeftColumn sx={{ pl: 4, pr: 4 }}>
            <Loader loading={documentsLoader}>
                {alerts.map(alert => (
                    <Alert
                        key={alert.id}
                        alert={alert}
                        secondIdCheckStatus={secondIdCheckStatus}
                        onAlertChange={getData}
                        isRemovable={alertsAreRemovable}
                        roleName={subscription?.role?.name}
                        subscription={subscription}
                        hideComment={
                            (!userIsServicer && !userIsProvider && !userIsPromoter) ||
                            userIsNetworkAdmin
                        }
                    />
                ))}
                {!alerts.length && (
                    <Typography align="center">{texts.NO_SUBSCRIPTION_ALERTS}</Typography>
                )}
                {(!isSubscriptionFinished || userIsServicer || userIsProvider) &&
                    !userIsStandardServicer && (
                        <Comments
                            subscription={subscription}
                            disableNewComment={userIsPromoter}
                            disableLeftPadding
                        />
                    )}
                {subscriptionCanBeValidated && (
                    <ActionButton
                        variant="contained"
                        sx={{ marginTop: 2, alignSelf: 'flex-start' }}
                        loading={isLoading}
                        onClick={handleValidateSubscription}
                    >
                        {subscription?.investmentFund?.evergreen
                            ? texts.VALIDATE_SUBSCRIPTION
                            : texts.READY_TO_SIGN_BY_SGP}
                    </ActionButton>
                )}
                {isValidated && (
                    <ValidationText
                        validatedAt={subscription.validatedByServicerAt}
                        validatedBy={subscription.validatedByServicer}
                        sx={{ marginLeft: 0 }}
                    />
                )}
            </Loader>
        </InfoBox>
    );
};

AlertsSection.propTypes = {
    subscription: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    getData: PropTypes.func,
};

export default React.memo(AlertsSection);
